import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors, typography } from '../../styles/theme';
import DashboardLayout from '../../components/layouts/dashboard/DashboardLayout';
import EditDialog from '../../components/layouts/dashboard/EditDialog';
import { auth } from '../../config/firebase';
import { useNavigate } from 'react-router-dom';
import { updatePassword, sendPasswordResetEmail, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import axios from 'axios';

const ProfileContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
`;

const SignOutButton = styled.button`
  color: #FF0000;
  background: none;
  border: none;
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  padding: 12px 0;
  cursor: pointer;
  display: block;
  margin: 16px auto 0;
  transition: opacity 0.2s ease;
  text-align: center;

  &:hover {
    opacity: 0.8;
  }
`;

const ProfileField = styled.div`
  background: white;
  border-radius: 8px;
  transition: all 0.2s ease;
  padding: 24px;
  &:hover {
    transform: translateY(-1px);
    background-color: rgba(0, 0, 0, 0.03);
  }
`;

const FieldHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const FieldLabel = styled.div`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
  margin-bottom: 8px;
`;

const FieldValue = styled.div`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  color: ${colors.text.primary};
  border-radius: 8px;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  color: ${colors.text.secondary};
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;

  &:hover {
    color: ${colors.primary};
  }

  ${ProfileField}:hover & {
    opacity: 1;
  }
`;

const ErrorMessage = styled.div`
  color: ${colors.error};
  text-align: center;
  padding: 16px;
  background: ${colors.error}10;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const PasswordButton = styled.button`
  background-color: ${colors.primary};
  color: white;
  border: none;
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  padding: 12px 32px;
  border-radius: 8px;
  cursor: pointer;
  display: block;
  margin: 32px auto 0;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${colors.primary}CC;
  }
`;

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const DialogContent = styled.div`
  background: white;
  padding: 32px;
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  position: relative;
`;

const DialogTitle = styled.h2`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.xl};
  color: ${colors.text.primary};
  margin-bottom: 24px;
`;

const PasswordInput = styled.input.attrs({ type: 'password' })`
  width: 100%;
  padding: 12px;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  margin-bottom: 16px;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
`;

const DialogButton = styled.button`
  padding: 8px 24px;
  border-radius: 8px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.primary ? `
    background-color: ${colors.primary};
    color: white;
    border: none;
    
    &:hover {
      background-color: ${colors.primary}CC;
    }
  ` : `
    background-color: transparent;
    color: ${colors.text.secondary};
    border: 1px solid ${colors.border};
    
    &:hover {
      background-color: ${colors.background};
    }
  `}
`;

const PasswordDialog = ({ 
  isOpen, 
  onClose, 
  onSave, 
  title, 
  type,
  currentPassword,
  setCurrentPassword,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword
}) => {
  if (!isOpen) return null;

  return (
    <DialogOverlay>
      <DialogContent>
        <DialogTitle>{title}</DialogTitle>
        <form onSubmit={(e) => {
          e.preventDefault();
          onSave();
        }}>
          {type === 'change' && (
            <PasswordInput
              placeholder="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          )}
          <PasswordInput
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <PasswordInput
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          <ButtonGroup>
            <DialogButton type="button" onClick={onClose}>Cancel</DialogButton>
            <DialogButton type="submit" primary>Save</DialogButton>
          </ButtonGroup>
        </form>
      </DialogContent>
    </DialogOverlay>
  );
};

const Profile = () => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    linkedinUrl: ''
  });
  const [error, setError] = useState('');
  const [editDialog, setEditDialog] = useState({ isOpen: false, field: null });
  const [passwordDialog, setPasswordDialog] = useState({ isOpen: false, type: null });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('No user is currently logged in');
        return;
      }

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/dashboard/profile/${currentUser.uid}`);
      
      if (!response.data.success) {
        setError(response.data.error || 'Failed to fetch profile data');
        return;
      }

      setProfileData(response.data.data);
    } catch (error) {
      console.error('Error fetching profile data:', error);
      setError('Failed to load profile data');
    }
  };

  const handleEdit = (field, value, label) => {
    setEditDialog({
      isOpen: true,
      field,
      value,
      label
    });
  };

  const handleSave = async (newValue) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/dashboard/profile/${currentUser.uid}/field`, {
        field: editDialog.field,
        value: newValue
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to update field');
      }

      // Update local state
      setProfileData(prev => ({
        ...prev,
        [editDialog.field]: newValue
      }));

      setEditDialog({ isOpen: false, field: null });
    } catch (err) {
      console.error('Error saving profile data:', err);
      setError('Failed to update profile');
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out');
    }
  };

  const handlePasswordAction = async () => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No user logged in');

      // Check if user has password provider
      const hasPasswordProvider = user.providerData?.some(provider => provider?.providerId === 'password');

      if (hasPasswordProvider) {
        // User has password - show change password dialog
        setPasswordDialog({ isOpen: true, type: 'change' });
      } else {
        // User doesn't have password - send reset email
        await sendPasswordResetEmail(auth, user.email);
        alert('Password setup email sent. Please check your inbox.');
      }
    } catch (error) {
      console.error('Password action error:', error);
      setError('Failed to process password action');
    }
  };

  const handlePasswordSubmit = async () => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No user logged in');

      // Validate password fields
      if (!newPassword || !confirmPassword) {
        throw new Error('Please fill in all password fields');
      }

      if (newPassword.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      if (newPassword !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (passwordDialog.type === 'change') {
        if (!currentPassword) {
          throw new Error('Please enter your current password');
        }
        // Re-authenticate user before changing password
        try {
          const credential = EmailAuthProvider.credential(user.email, currentPassword);
          await reauthenticateWithCredential(user, credential);
        } catch (authError) {
          if (authError.code === 'auth/wrong-password') {
            throw new Error('Current password is incorrect');
          }
          throw new Error('Authentication failed. Please try again.');
        }
      }

      // Update password
      await updatePassword(user, newPassword);

      setPasswordDialog({ isOpen: false, type: null });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      alert('Password updated successfully');
    } catch (error) {
      console.error('Password update error:', error);
      setError(error.message || 'Failed to update password');
    }
  };

  const renderProfileField = (field, label) => (
    <ProfileField>
      <FieldHeader>
        <FieldLabel>{label}</FieldLabel>
        <EditButton onClick={() => handleEdit(field, profileData[field], label)}>✎</EditButton>
      </FieldHeader>
      <FieldValue>{profileData[field] || 'Please add your details'}</FieldValue>
    </ProfileField>
  );

  return (
    <DashboardLayout title="Profile">
      <ProfileContainer>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        {renderProfileField('name', 'Name')}
        {renderProfileField('email', 'Email')}
        {renderProfileField('phone', 'Phone')}
        {renderProfileField('linkedinUrl', 'LinkedIn URL')}

        <PasswordButton onClick={handlePasswordAction}>
          {auth.currentUser?.providerData?.[0]?.providerId === 'password' 
            ? 'Change Password' 
            : 'Set Up Password'}
        </PasswordButton>

        <SignOutButton onClick={handleSignOut}>
          Sign Out
        </SignOutButton>
      </ProfileContainer>

      <EditDialog
        isOpen={editDialog.isOpen}
        onClose={() => setEditDialog({ isOpen: false, field: null })}
        onSave={handleSave}
        title={`Edit ${editDialog.label}`}
        fieldLabel={editDialog.label}
        initialValue={editDialog.value}
        field={editDialog.field}
      />

      <PasswordDialog
        isOpen={passwordDialog.isOpen}
        onClose={() => {
          setPasswordDialog({ isOpen: false, type: null });
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setError('');
        }}
        onSave={handlePasswordSubmit}
        title={passwordDialog.type === 'change' ? 'Change Password' : 'Set Password'}
        type={passwordDialog.type}
        currentPassword={currentPassword}
        setCurrentPassword={setCurrentPassword}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        confirmPassword={confirmPassword}
        setConfirmPassword={setConfirmPassword}
      />
    </DashboardLayout>
  );
};

export default Profile; 